import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { Modal, Button, Tooltip, Flex, ActionIcon, Menu } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  IconCalendarCheck,
  IconCalendarUser,
  IconCalendarX,
  IconMenuDeep,
} from "@tabler/icons-react";
import { type Schemas } from "@/types";
import { useEntityUpdateMutation } from "@/features/entity/mutations";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "react-query";

interface CloseAppointmentButtonsProps {
  currentState?: string;
  appointmentId?: string;
  appointmentType?: string;
  rentableItemId?: string;
  disabled?: boolean;
  isAdmin?: boolean;
}

export function CloseAppointmentButtons({
  currentState,
  appointmentId,
  appointmentType,
  rentableItemId,
  disabled,
  isAdmin,
}: CloseAppointmentButtonsProps) {
  const [
    confirmationOpened,
    { open: openConfirmation, close: closeConfirmation },
  ] = useDisclosure(false);
  const queryCache = useQueryClient();
  const [pendingState, setPendingState] = useState<
    "Open" | "Completed" | "NoShow" | "Cancelled" | undefined
  >(undefined);
  const { t } = useTranslation("features");
  const { mutate: update } = useEntityUpdateMutation<
    Schemas["Appointment"],
    Schemas["AppointmentPatchDto"]
  >({
    resourcePath: "/api/Appointments/{id}",
    resourceId: appointmentId ?? "",

    queryKey: "appointment",
  });

  const isAdminViewingBlockCalendar =
    appointmentType == "BlockCalendar" && isAdmin;

  const appointmentGroupTypeTransport =
    appointmentType == "Trailer" ||
    appointmentType == "MovingVan" ||
    appointmentType == "MovingHelp";

  const isMovingHelp = appointmentType == "MovingHelp";

  const rentableItemMaintenance =
    appointmentType == "BlockCalendar" &&
    rentableItemId &&
    rentableItemId.trim() !== "";

  const handleUpdateAppointmentState = (
    state: "Open" | "Completed" | "NoShow" | "Cancelled" | undefined,
  ) => {
    if (!state) return;

    update(
      {
        appointmentStatus: state,
        recordState: "Inactive",
      },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("appointments.updateStateSuccessTitle"),
            message: t("appointments.updateStateSuccessMessage"),
          });
          void queryCache.invalidateQueries("appointment_list");
        },
        onError: () => {
          notifications.show({
            color: "red",
            title: t("appointments.updateStateErrorTitle"),
            message: t("appointments.updateStateErrorMessage"),
          });
        },
      },
    );
    closeConfirmation();
  };

  const handleQuickAction = (
    state: "Open" | "Completed" | "NoShow" | "Cancelled" | undefined,
  ) => {
    setPendingState(state);
    openConfirmation();
  };

  return (
    <Flex>
      <Menu position="bottom-end" withArrow>
        <Menu.Target>
          <Tooltip label={t("appointments.changeStatusButton")}>
            <ActionIcon
              variant="filled"
              color="primary"
              size="lg"
              disabled={disabled}
            >
              <IconMenuDeep style={{ width: "60%", height: "60%" }} />
            </ActionIcon>
          </Tooltip>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Appointment Status</Menu.Label>
          {(currentState == "Open" || currentState == "Rescheduled") &&
            !rentableItemMaintenance &&
            !isAdminViewingBlockCalendar && (
              <>
                <Menu.Item
                  leftSection={<IconCalendarCheck size={14} />}
                  onClick={() => handleQuickAction("Completed")}
                >
                  {t("appointments.setAppointmentCompleted")}
                </Menu.Item>
                {!isMovingHelp && (
                  <Menu.Item
                    leftSection={<IconCalendarUser size={14} />}
                    onClick={() => handleQuickAction("NoShow")}
                  >
                    {t("appointments.setAppointmentNoShow")}
                  </Menu.Item>
                )}
              </>
            )}
          {currentState == "NoShow" && !rentableItemMaintenance && (
            <Menu.Item
              leftSection={<IconCalendarCheck size={14} />}
              onClick={() => handleQuickAction("Completed")}
            >
              {t("appointments.setAppointmentCompletedFromNoShow")}
            </Menu.Item>
          )}
          {(appointmentGroupTypeTransport ||
            rentableItemMaintenance ||
            isAdminViewingBlockCalendar) && (
            <>
              <Menu.Divider />
              <Menu.Item
                color="red"
                leftSection={<IconCalendarX size={14} />}
                onClick={() => handleQuickAction("Cancelled")}
              >
                {t("appointments.setAppointmentCancelled")}
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={confirmationOpened}
        onClose={closeConfirmation}
        centered
        title={
          pendingState == "Completed"
            ? t("appointments.confirmCompleted")
            : pendingState == "NoShow"
              ? t("appointments.confirmNoShow")
              : pendingState == "Cancelled"
                ? t("appointments.confirmCancelled")
                : ""
        }
      >
        <Flex gap="xs">
          <Button
            onClick={closeConfirmation}
            variant="light"
            w={"48%"}
            mt={"xs"}
          >
            {t("appointments.cancelButton")}
          </Button>
          <Button
            onClick={() => handleUpdateAppointmentState(pendingState)}
            w={"48%"}
            mt={"xs"}
          >
            {t("appointments.ok")}
          </Button>
        </Flex>
      </Modal>
    </Flex>
  );
}
